import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart,
} from 'recharts';
import {
  getWearableSummary,
  getDailyMetrics,
  getSleepData,
  getWorkouts,
  getGoals,
  getSources,
  getOuraStatus,
  getStravaStatus,
  connectOura,
  disconnectOura,
  syncOura,
  getStravaAuthUrl,
  disconnectStrava,
  syncStrava,
  handleStravaCallback,
  getGarminStatus,
  connectGarmin,
  disconnectGarmin,
  syncGarmin,
  importCSV,
  exportData,
  deleteBySource,
  createDailyMetric,
  createSleepRecord,
  createWorkout,
  createGoal,
  deleteGoal,
  deleteWorkout,
  formatSleepDuration,
  formatDistance,
  getSourceLabel,
  getSourceColor,
  formatDateItalian,
  workoutTypes,
  goalTypes,
  type DailyMetric,
  type SleepData,
  type Workout,
  type HealthGoal,
  type OuraStatus,
  type StravaStatus,
  type GarminStatus,
  type HeartRateData,
  getHeartRateData,
} from '../api/wearable';

type TabType = 'panoramica' | 'passi' | 'sonno' | 'frequenzaCardiaca' | 'allenamenti' | 'integrazioni' | 'obiettivi';

const CHART_COLORS = {
  steps: '#3B82F6',
  calories: '#EF4444',
  distance: '#10B981',
  activeMinutes: '#F59E0B',
  sleep: '#8B5CF6',
  deep: '#6366F1',
  light: '#A5B4FC',
  rem: '#C4B5FD',
  awake: '#FBBF24',
  heartRate: '#EF4444',
  oura: '#8B5CF6',    // Purple for Oura
  strava: '#F97316',  // Orange for Strava
  garmin: '#22C55E',  // Green for Garmin
};

export function Wearable() {
  const [activeTab, setActiveTab] = useState<TabType>('panoramica');
  const [loading, setLoading] = useState(true);
  const [dailyMetrics, setDailyMetrics] = useState<DailyMetric[]>([]);
  const [sleepData, setSleepData] = useState<SleepData[]>([]);
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [goals, setGoals] = useState<HealthGoal[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [ouraStatus, setOuraStatus] = useState<OuraStatus>({ connected: false });
  const [stravaStatus, setStravaStatus] = useState<StravaStatus>({ connected: false });
  const [garminStatus, setGarminStatus] = useState<GarminStatus>({ connected: false });
  const [showGarminModal, setShowGarminModal] = useState(false);
  const [heartRateData, setHeartRateData] = useState<HeartRateData[]>([]);

  // Source filtering
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [defaultSource, setDefaultSource] = useState<string | null>(() => {
    // Load from localStorage
    return localStorage.getItem('wearable_default_source');
  });

  // Time filtering for heart rate (optional, empty = all times)
  const [hrStartTime, setHrStartTime] = useState<string>('');
  const [hrEndTime, setHrEndTime] = useState<string>('');

  // Toggle source filter
  const toggleSourceFilter = (source: string) => {
    setSelectedSources(prev =>
      prev.includes(source)
        ? prev.filter(s => s !== source)
        : [...prev, source]
    );
  };

  // Set default source for redundancy resolution
  const handleSetDefaultSource = (source: string | null) => {
    setDefaultSource(source);
    if (source) {
      localStorage.setItem('wearable_default_source', source);
    } else {
      localStorage.removeItem('wearable_default_source');
    }
  };

  // Filter data by selected sources (or all if none selected)
  const filterBySource = <T extends { source: string }>(data: T[]): T[] => {
    if (selectedSources.length === 0) return data;
    return data.filter(d => selectedSources.includes(d.source));
  };

  // Get unique data for a date, preferring default source for redundancy
  const deduplicateByDate = <T extends { source: string }>(
    data: T[],
    getDate: (item: T) => string
  ): T[] => {
    const dateMap = new Map<string, T[]>();

    // Group by date
    for (const item of data) {
      const date = getDate(item);
      if (!dateMap.has(date)) {
        dateMap.set(date, []);
      }
      dateMap.get(date)!.push(item);
    }

    // For each date, pick the best item (default source or first)
    const result: T[] = [];
    for (const [, items] of dateMap) {
      if (items.length === 1) {
        result.push(items[0]);
      } else {
        // Multiple sources for same date - prefer default source
        const preferred = defaultSource
          ? items.find(i => i.source === defaultSource)
          : null;
        result.push(preferred || items[0]);
      }
    }

    return result;
  };

  // Detect dates with multiple sources (redundancy)
  const getRedundantDates = <T extends { source: string }>(
    data: T[],
    getDate: (item: T) => string
  ): Set<string> => {
    const dateSourceMap = new Map<string, Set<string>>();

    for (const item of data) {
      const date = getDate(item);
      if (!dateSourceMap.has(date)) {
        dateSourceMap.set(date, new Set());
      }
      dateSourceMap.get(date)!.add(item.source);
    }

    const redundant = new Set<string>();
    for (const [date, sources] of dateSourceMap) {
      if (sources.size > 1) {
        redundant.add(date);
      }
    }

    return redundant;
  };

  // Get colors of sources that have overlapping data (for stripe patterns)
  const getRedundantSourceColors = <T extends { source: string }>(
    data: T[],
    getDate: (item: T) => string
  ): string[] => {
    const sourcesWithRedundancy = new Set<string>();
    const dateSourceMap = new Map<string, Set<string>>();

    for (const item of data) {
      const date = getDate(item);
      if (!dateSourceMap.has(date)) {
        dateSourceMap.set(date, new Set());
      }
      dateSourceMap.get(date)!.add(item.source);
    }

    // Collect all sources that appear in redundant dates
    for (const [, sources] of dateSourceMap) {
      if (sources.size > 1) {
        for (const source of sources) {
          sourcesWithRedundancy.add(source);
        }
      }
    }

    // Convert to color array using CHART_COLORS
    const sourceColorMap: Record<string, string> = {
      oura: CHART_COLORS.oura,
      strava: CHART_COLORS.strava,
      garmin: CHART_COLORS.garmin,
    };

    return Array.from(sourcesWithRedundancy)
      .map(source => sourceColorMap[source] || CHART_COLORS.steps)
      .slice(0, 2); // Max 2 colors for stripe pattern
  };

  // Date range
  const [dateRange, setDateRange] = useState({ days: 30 });
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Modals
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualEntryType, setManualEntryType] = useState<'daily' | 'sleep' | 'workout'>('daily');
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showOuraModal, setShowOuraModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [_summaryRes, metricsRes, sleepRes, workoutsRes, goalsRes, sourcesRes, ouraRes, stravaRes, garminRes, heartRateRes] = await Promise.all([
        getWearableSummary(dateRange.days), // Keep for API consistency
        getDailyMetrics({ startDate, endDate }),
        getSleepData({ startDate, endDate }),
        getWorkouts({ startDate, endDate }),
        getGoals(),
        getSources(),
        getOuraStatus(),
        getStravaStatus(),
        getGarminStatus(),
        getHeartRateData({ startDate, endDate }),
      ]);

      // Summary is now calculated from filtered data (filteredSummary)
      setDailyMetrics(metricsRes.metrics);
      setSleepData(sleepRes.sleep);
      setWorkouts(workoutsRes.workouts);
      setGoals(goalsRes.goals);
      setSources(sourcesRes.sources);
      setOuraStatus(ouraRes);
      setStravaStatus(stravaRes);
      setGarminStatus(garminRes);
      setHeartRateData(heartRateRes.heartRate || []);
    } catch (error) {
      console.error('Error loading wearable data:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange.days, startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Check for Strava callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && state === 'strava') {
      handleStravaCallback(code)
        .then(() => {
          window.history.replaceState({}, '', window.location.pathname);
          loadData();
        })
        .catch(console.error);
    }
  }, []);

  // Handlers
  const handleOuraConnect = async (token: string) => {
    try {
      await connectOura(token);
      setShowOuraModal(false);
      await loadData();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Errore nella connessione');
    }
  };

  const handleOuraDisconnect = async () => {
    try {
      await disconnectOura();
      await loadData();
    } catch (error) {
      console.error(error);
    }
  };

  const handleStravaConnect = async () => {
    try {
      const res = await getStravaAuthUrl();
      window.location.href = res.authUrl + '&state=strava';
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Strava non configurato');
    }
  };

  const handleStravaDisconnect = async () => {
    try {
      await disconnectStrava();
      await loadData();
    } catch (error) {
      console.error(error);
    }
  };

  const handleGarminConnect = async (username: string, password: string) => {
    try {
      await connectGarmin(username, password);
      setShowGarminModal(false);
      await loadData();
    } catch (error) {
      throw error; // Re-throw per gestione nel modal
    }
  };

  const handleGarminDisconnect = async () => {
    try {
      await disconnectGarmin();
      await loadData();
    } catch (error) {
      console.error(error);
    }
  };

  const handleSync = async (provider: 'oura' | 'strava' | 'garmin') => {
    setSyncing(true);
    const labels: Record<string, string> = {
      oura: 'Oura Ring',
      strava: 'Strava',
      garmin: 'Garmin Connect',
    };
    setSyncMessage(`Sincronizzazione ${labels[provider]} in corso...`);
    try {
      const syncFn = provider === 'oura' ? syncOura : provider === 'strava' ? syncStrava : syncGarmin;
      const result = await syncFn(startDate, endDate);
      const total = Object.values(result.imported).reduce((a, b) => a + b, 0);
      setSyncMessage(`Sincronizzati ${total} record`);
      await loadData();
    } catch (error) {
      setSyncMessage(error instanceof Error ? error.message : 'Errore');
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMessage(''), 3000);
    }
  };

  // Sync full history (365 days)
  const handleSyncFullHistory = async (provider: 'oura' | 'strava' | 'garmin') => {
    setSyncing(true);
    const labels: Record<string, string> = {
      oura: 'Oura Ring',
      strava: 'Strava',
      garmin: 'Garmin Connect',
    };
    setSyncMessage(`Sincronizzazione storico completo ${labels[provider]}...`);
    try {
      const syncFn = provider === 'oura' ? syncOura : provider === 'strava' ? syncStrava : syncGarmin;
      const end = new Date();
      const start = new Date();
      start.setFullYear(start.getFullYear() - 1); // 1 year back
      const startStr = start.toISOString().split('T')[0];
      const endStr = end.toISOString().split('T')[0];
      const result = await syncFn(startStr, endStr);
      const total = Object.values(result.imported).reduce((a, b) => a + b, 0);
      setSyncMessage(`Sincronizzati ${total} record (storico completo)`);
      await loadData();
    } catch (error) {
      setSyncMessage(error instanceof Error ? error.message : 'Errore');
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMessage(''), 5000);
    }
  };

  // Sync all connected providers
  const handleSyncAll = async () => {
    const providers: ('oura' | 'strava' | 'garmin')[] = [];
    if (ouraStatus.connected) providers.push('oura');
    if (stravaStatus.connected) providers.push('strava');
    if (garminStatus.connected) providers.push('garmin');

    if (providers.length === 0) {
      setSyncMessage('Nessun dispositivo collegato');
      setTimeout(() => setSyncMessage(''), 3000);
      return;
    }

    setSyncing(true);
    setSyncMessage(`Sincronizzazione di ${providers.length} dispositivi...`);
    let totalRecords = 0;
    const errors: string[] = [];

    const providerLabels: Record<string, string> = {
      oura: 'Oura Ring',
      strava: 'Strava',
      garmin: 'Garmin Connect',
    };

    for (const provider of providers) {
      try {
        setSyncMessage(`Sincronizzazione ${providerLabels[provider]}...`);
        const syncFn = provider === 'oura' ? syncOura : provider === 'strava' ? syncStrava : syncGarmin;
        const result = await syncFn(startDate, endDate);
        const total = Object.values(result.imported).reduce((a, b) => a + b, 0);
        totalRecords += total;
      } catch (error) {
        errors.push(`${provider}: ${error instanceof Error ? error.message : 'Errore'}`);
      }
    }

    await loadData();
    setSyncing(false);

    if (errors.length > 0) {
      setSyncMessage(`Sincronizzati ${totalRecords} record. Errori: ${errors.join(', ')}`);
    } else {
      setSyncMessage(`Sincronizzati ${totalRecords} record da ${providers.length} dispositivi`);
    }
    setTimeout(() => setSyncMessage(''), 5000);
  };

  const handleImportCSV = async (file: File) => {
    try {
      const result = await importCSV(file);
      const total = Object.values(result.imported).filter(v => typeof v === 'number').reduce((a: number, b) => a + (b as number), 0);
      alert(`Importati ${total} record da ${result.imported.source}`);
      setShowImportModal(false);
      await loadData();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Errore durante l\'import');
    }
  };

  const handleExport = async () => {
    try {
      const result = await exportData({ startDate, endDate });
      const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wearable-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setShowExportModal(false);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Errore durante l\'export');
    }
  };

  const handleDeleteSource = async (source: string) => {
    try {
      const result = await deleteBySource(source);
      const total = Object.values(result.deleted).reduce((a, b) => a + b, 0);
      alert(`Eliminati ${total} record da ${getSourceLabel(source)}`);
      setShowDeleteConfirm(null);
      await loadData();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Errore');
    }
  };

  // Filtered data based on selected sources
  const filteredDailyMetrics = filterBySource(dailyMetrics);
  const filteredSleepData = filterBySource(sleepData);
  const filteredWorkouts = filterBySource(workouts);

  // Detect redundant dates (multiple sources for same day)
  const redundantMetricDates = getRedundantDates(filteredDailyMetrics, m => m.metricDate);
  const redundantSleepDates = getRedundantDates(filteredSleepData, s => s.sleepDate);
  const hasRedundancy = redundantMetricDates.size > 0 || redundantSleepDates.size > 0;

  // Get colors for stripe patterns based on filtered sources with redundancy
  const metricStripeColors = getRedundantSourceColors(filteredDailyMetrics, m => m.metricDate);
  const sleepStripeColors = getRedundantSourceColors(filteredSleepData, s => s.sleepDate);
  const stripeColor1 = metricStripeColors[0] || sleepStripeColors[0] || CHART_COLORS.oura;
  const stripeColor2 = metricStripeColors[1] || sleepStripeColors[1] || CHART_COLORS.strava;

  // Deduplicated data for summary calculations (uses default source preference)
  const deduplicatedMetrics = deduplicateByDate(filteredDailyMetrics, m => m.metricDate);
  const deduplicatedSleep = deduplicateByDate(filteredSleepData, s => s.sleepDate);

  // Chart data preparation with source info
  const stepsChartData = filteredDailyMetrics
    .slice()
    .reverse()
    .map((m, index) => ({
      // Unique key for x-axis to prevent Recharts from grouping same dates
      xKey: `${index}_${m.metricDate}_${m.source}`,
      date: new Date(m.metricDate).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
      fullDate: m.metricDate,
      passi: m.steps || 0,
      calorie: m.caloriesActive || 0,
      distanza: m.distance ? parseFloat(m.distance.toFixed(2)) : 0,
      minutiAttivi: m.activeMinutes || 0,
      source: m.source,
      isRedundant: redundantMetricDates.has(m.metricDate),
    }));

  const sleepChartData = filteredSleepData
    .slice()
    .reverse()
    .map((s, index) => ({
      xKey: `${index}_${s.sleepDate}_${s.source}`,
      date: new Date(s.sleepDate).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
      fullDate: s.sleepDate,
      totale: s.totalSleepMinutes ? Math.round(s.totalSleepMinutes / 60 * 10) / 10 : 0,
      profondo: s.deepSleepMinutes ? Math.round(s.deepSleepMinutes / 60 * 10) / 10 : 0,
      leggero: s.lightSleepMinutes ? Math.round(s.lightSleepMinutes / 60 * 10) / 10 : 0,
      rem: s.remSleepMinutes ? Math.round(s.remSleepMinutes / 60 * 10) / 10 : 0,
      hrv: s.hrv || 0,
      source: s.source,
      isRedundant: redundantSleepDates.has(s.sleepDate),
    }));

  // Calculate filtered summary
  const filteredSummary = {
    steps: {
      average: deduplicatedMetrics.length > 0
        ? Math.round(deduplicatedMetrics.reduce((sum, m) => sum + (m.steps || 0), 0) / deduplicatedMetrics.length)
        : 0,
      latest: filteredDailyMetrics[0]?.steps || 0,
    },
    sleep: {
      averageMinutes: deduplicatedSleep.length > 0
        ? Math.round(deduplicatedSleep.reduce((sum, s) => sum + (s.totalSleepMinutes || 0), 0) / deduplicatedSleep.length)
        : 0,
      avgHRV: deduplicatedSleep.length > 0
        ? Math.round(deduplicatedSleep.filter(s => s.hrv).reduce((sum, s) => sum + (s.hrv || 0), 0) / deduplicatedSleep.filter(s => s.hrv).length) || 0
        : 0,
    },
    workouts: {
      total: filteredWorkouts.length,
      totalMinutes: filteredWorkouts.reduce((sum, w) => sum + w.duration, 0),
    },
  };

  const workoutsByType = filteredWorkouts.reduce((acc, w) => {
    acc[w.workoutType] = (acc[w.workoutType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const workoutPieData = Object.entries(workoutsByType).map(([name, value]) => ({
    name,
    value,
  }));

  const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

  // Filter heart rate data by selected sources and time range
  const filteredHeartRateData = heartRateData.filter(hr => {
    // Source filter
    if (selectedSources.length > 0 && !selectedSources.includes(hr.source)) {
      return false;
    }

    // Time filter (if set)
    if (hrStartTime || hrEndTime) {
      const hrTime = new Date(hr.timestamp);
      const hrHour = hrTime.getHours();
      const hrMinute = hrTime.getMinutes();
      const hrTimeMinutes = hrHour * 60 + hrMinute;

      if (hrStartTime) {
        const [startHour, startMin] = hrStartTime.split(':').map(Number);
        const startMinutes = startHour * 60 + startMin;
        if (hrTimeMinutes < startMinutes) return false;
      }

      if (hrEndTime) {
        const [endHour, endMin] = hrEndTime.split(':').map(Number);
        const endMinutes = endHour * 60 + endMin;
        if (hrTimeMinutes > endMinutes) return false;
      }
    }

    return true;
  });

  // Calculate if date range is <= 2 days (for showing actual HR values vs max)
  // Note: "1g" button sets yesterday-to-today which spans 2 calendar days
  const isShortRange = (() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMs = end.getTime() - start.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays <= 2;
  })();

  // Prepare heart rate chart data - separate lines for each source
  // Creates unified timeline with ouraHR, stravaHR, and garminHR fields
  // When date range <= 1 day, show actual HR values; otherwise show max values
  const heartRateChartData = (() => {
    // Create a map with all timestamps and their data by source
    const dataMap = new Map<number, {
      timestamp: string;
      ouraHR: number | null;
      stravaHR: number | null;
      garminHR: number | null;
      ouraData?: typeof filteredHeartRateData[0];
      stravaData?: typeof filteredHeartRateData[0];
      garminData?: typeof filteredHeartRateData[0];
    }>();

    filteredHeartRateData.forEach(hr => {
      const ts = new Date(hr.timestamp).getTime();
      if (!dataMap.has(ts)) {
        dataMap.set(ts, {
          timestamp: hr.timestamp,
          ouraHR: null,
          stravaHR: null,
          garminHR: null,
        });
      }
      const entry = dataMap.get(ts)!;
      // Use actual HR for short ranges, max HR for longer ranges
      const hrValue = isShortRange ? hr.heartRate : (hr.maxHeartRate ?? hr.heartRate);

      if (hr.source === 'oura') {
        entry.ouraHR = hrValue;
        entry.ouraData = hr;
      } else if (hr.source === 'strava') {
        entry.stravaHR = hrValue;
        entry.stravaData = hr;
      } else if (hr.source === 'garmin') {
        entry.garminHR = hrValue;
        entry.garminData = hr;
      }
    });

    // Sort by timestamp and convert to array
    return Array.from(dataMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([, data], index) => {
        const dateObj = new Date(data.timestamp);
        return {
          xKey: `${index}_${data.timestamp}`,
          timestamp: data.timestamp,
          date: dateObj.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
          time: dateObj.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
          fullDateTime: `${dateObj.toLocaleDateString('it-IT')} ${dateObj.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`,
          ouraHR: data.ouraHR,
          stravaHR: data.stravaHR,
          garminHR: data.garminHR,
          ouraData: data.ouraData,
          stravaData: data.stravaData,
          garminData: data.garminData,
        };
      });
  })();

  // Calculate heart rate statistics
  const heartRateStats = {
    avg: filteredHeartRateData.length > 0
      ? Math.round(filteredHeartRateData.reduce((sum, hr) => sum + hr.heartRate, 0) / filteredHeartRateData.length)
      : 0,
    min: filteredHeartRateData.length > 0
      ? Math.min(...filteredHeartRateData.map(hr => hr.heartRate))
      : 0,
    max: filteredHeartRateData.length > 0
      ? Math.max(...filteredHeartRateData.map(hr => hr.heartRate))
      : 0,
    count: filteredHeartRateData.length,
  };

  const tabs: { id: TabType; label: string; icon: string }[] = [
    { id: 'panoramica', label: 'Panoramica', icon: '📊' },
    { id: 'passi', label: 'Attività', icon: '🚶' },
    { id: 'sonno', label: 'Sonno', icon: '😴' },
    { id: 'frequenzaCardiaca', label: 'Frequenza Cardiaca', icon: '❤️' },
    { id: 'allenamenti', label: 'Allenamenti', icon: '💪' },
    { id: 'obiettivi', label: 'Obiettivi', icon: '🎯' },
    { id: 'integrazioni', label: 'Integrazioni', icon: '🔗' },
  ];

  return (
    <Layout title="Wearable">
      <div className="max-w-7xl mx-auto">
        {/* Date Range Selector */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Da:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">A:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
              />
            </div>
            <div className="flex gap-2">
              {[1, 7, 14, 30, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => {
                    const end = new Date();
                    const start = new Date();
                    start.setDate(start.getDate() - days);
                    setStartDate(start.toISOString().split('T')[0]);
                    setEndDate(end.toISOString().split('T')[0]);
                    setDateRange({ days });
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    dateRange.days === days
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {days}g
                </button>
              ))}
            </div>
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => setShowImportModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
              >
                Import CSV
              </button>
              <button
                onClick={() => setShowExportModal(true)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700"
              >
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="flex overflow-x-auto border-b">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
          </div>
        ) : (
          <>
            {/* Panoramica Tab */}
            {activeTab === 'panoramica' && (
              <div className="space-y-6">
                {/* Source Filter Bar */}
                {sources.length > 1 && (
                  <div className="bg-white rounded-xl shadow-sm p-4">
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Filtra per sorgente:</span>
                        <div className="flex flex-wrap gap-2">
                          {sources.map((source) => {
                            const color = getSourceColor(source);
                            const isSelected = selectedSources.length === 0 || selectedSources.includes(source);
                            return (
                              <button
                                key={source}
                                onClick={() => toggleSourceFilter(source)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                  isSelected
                                    ? `${color.bg} ${color.text} ring-2 ring-offset-1 ring-${color.text.replace('text-', '')}`
                                    : 'bg-gray-100 text-gray-400'
                                }`}
                              >
                                {getSourceLabel(source)}
                                {isSelected && selectedSources.length > 0 && ' ✓'}
                              </button>
                            );
                          })}
                          {selectedSources.length > 0 && (
                            <button
                              onClick={() => setSelectedSources([])}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-200 text-gray-600 hover:bg-gray-300"
                            >
                              Mostra tutti
                            </button>
                          )}
                        </div>
                      </div>
                      {hasRedundancy && (
                        <div className="flex items-center gap-2 ml-auto">
                          <span className="text-amber-600 text-sm">⚠️ Dati ridondanti rilevati</span>
                          <select
                            value={defaultSource || ''}
                            onChange={(e) => handleSetDefaultSource(e.target.value || null)}
                            className="px-2 py-1 text-xs border rounded-lg"
                          >
                            <option value="">Sorgente default: auto</option>
                            {sources.map((source) => (
                              <option key={source} value={source}>
                                Default: {getSourceLabel(source)}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                    {hasRedundancy && !defaultSource && (
                      <p className="text-xs text-amber-600 mt-2">
                        💡 Alcune date hanno dati da più sorgenti. Seleziona una sorgente default per le medie o filtra per una singola sorgente.
                      </p>
                    )}
                  </div>
                )}

                {/* Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-blue-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Passi (media)</h3>
                    <p className="text-4xl font-bold text-blue-600">{filteredSummary.steps.average.toLocaleString('it-IT')}</p>
                    <p className="text-sm text-gray-600 mt-1">Ultimo: {filteredSummary.steps.latest?.toLocaleString('it-IT') || 0}</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Sonno (media)</h3>
                    <p className="text-4xl font-bold text-purple-600">{formatSleepDuration(filteredSummary.sleep.averageMinutes)}</p>
                    <p className="text-sm text-gray-600 mt-1">HRV medio: {filteredSummary.sleep.avgHRV || '-'} ms</p>
                  </div>
                  <div className="bg-orange-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Allenamenti</h3>
                    <p className="text-4xl font-bold text-orange-600">{filteredSummary.workouts.total}</p>
                    <p className="text-sm text-gray-600 mt-1">{filteredSummary.workouts.totalMinutes} min totali</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Sorgenti</h3>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {sources.map((source) => {
                        const color = getSourceColor(source);
                        const isFiltered = selectedSources.length > 0 && !selectedSources.includes(source);
                        return (
                          <button
                            key={source}
                            onClick={() => toggleSourceFilter(source)}
                            className={`px-2 py-1 rounded text-xs font-medium transition-opacity ${color.bg} ${color.text} ${isFiltered ? 'opacity-40' : ''}`}
                          >
                            {getSourceLabel(source)}
                            {defaultSource === source && ' ⭐'}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Steps Chart */}
                {stepsChartData.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Passi Giornalieri</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={stepsChartData}>
                          <defs>
                            <pattern id="stripePatternPanoramica" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                              <rect width="4" height="8" fill={stripeColor1} />
                              <rect x="4" width="4" height="8" fill={stripeColor2} />
                            </pattern>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="xKey"
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => {
                              const item = stepsChartData.find(d => d.xKey === value);
                              return item?.date || '';
                            }}
                          />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-white p-3 shadow-lg rounded-lg border">
                                    <p className="font-medium">{data.date}</p>
                                    <p className="text-blue-600">{data.passi?.toLocaleString('it-IT')} passi</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Sorgente: {getSourceLabel(data.source)}
                                      {data.isRedundant && <span className="text-amber-500 ml-1">⚠️ ridondante</span>}
                                    </p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar
                            dataKey="passi"
                            name="Passi"
                            fill={CHART_COLORS.steps}
                            shape={(props: unknown) => {
                              const { x, y, width, height, payload } = props as {
                                x: number; y: number; width: number; height: number;
                                payload: { isRedundant: boolean; source: string };
                              };
                              const fill = payload.isRedundant
                                ? 'url(#stripePatternPanoramica)'
                                : getSourceColor(payload.source).hex || CHART_COLORS.steps;
                              return <rect x={x} y={y} width={width} height={height} fill={fill} rx={2} />;
                            }}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    {redundantMetricDates.size > 0 && (
                      <p className="text-xs text-gray-600 mt-2">
                        <span className="inline-block w-4 h-3 mr-1 align-middle rounded-sm" style={{background: `repeating-linear-gradient(45deg, ${stripeColor1}, ${stripeColor1} 2px, ${stripeColor2} 2px, ${stripeColor2} 4px)`}}></span>
                        Le barre a strisce indicano giorni con dati da più sorgenti
                      </p>
                    )}
                  </div>
                )}

                {/* Sleep Chart */}
                {sleepChartData.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Sonno (ore)</h3>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={sleepChartData} margin={{ bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="xKey"
                            interval={0}
                            tick={(props: { x: number; y: number; payload: { value: string }; index: number }) => {
                              const item = sleepChartData.find(d => d.xKey === props.payload.value);
                              const sourceColor = item ? getSourceColor(item.source).hex : '#999';
                              // Check if previous item has same date (to avoid duplicate date labels)
                              const prevItem = props.index > 0 ? sleepChartData[props.index - 1] : null;
                              const showDateLabel = !prevItem || prevItem.date !== item?.date;
                              return (
                                <g transform={`translate(${props.x},${props.y})`}>
                                  <text x={0} y={0} dy={12} textAnchor="middle" fill="#666" fontSize={9}>
                                    {showDateLabel ? (item?.date || '') : ''}
                                  </text>
                                  <circle cx={0} cy={24} r={4} fill={sourceColor} />
                                </g>
                              );
                            }}
                            height={40}
                          />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                const sourceColor = getSourceColor(data.source);
                                return (
                                  <div className="bg-white p-3 shadow-lg rounded-lg border">
                                    <p className="font-medium">{data.date}</p>
                                    <p className="text-purple-600">Totale: {data.totale}h</p>
                                    <p className="text-xs text-indigo-600">Profondo: {data.profondo}h</p>
                                    <p className="text-xs text-indigo-400">Leggero: {data.leggero}h</p>
                                    <p className="text-xs text-purple-400">REM: {data.rem}h</p>
                                    {data.hrv > 0 && <p className="text-xs text-gray-600">HRV: {data.hrv}ms</p>}
                                    <p className="text-xs mt-1">
                                      <span className={`inline-block px-1.5 py-0.5 rounded ${sourceColor.bg} ${sourceColor.text}`}>
                                        {getSourceLabel(data.source)}
                                      </span>
                                      {data.isRedundant && <span className="text-amber-500 ml-1">⚠️ ridondante</span>}
                                    </p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Area type="monotone" dataKey="profondo" stackId="1" stroke={CHART_COLORS.deep} fill={CHART_COLORS.deep} name="Profondo" />
                          <Area type="monotone" dataKey="leggero" stackId="1" stroke={CHART_COLORS.light} fill={CHART_COLORS.light} name="Leggero" />
                          <Area type="monotone" dataKey="rem" stackId="1" stroke={CHART_COLORS.rem} fill={CHART_COLORS.rem} name="REM" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Source legend */}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS.oura }}></span>
                        Oura
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS.garmin }}></span>
                        Garmin
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS.strava }}></span>
                        Strava
                      </span>
                    </div>
                  </div>
                )}

                {/* Recent Workouts */}
                {filteredWorkouts.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Allenamenti Recenti</h3>
                    <div className="space-y-3">
                      {filteredWorkouts.slice(0, 5).map((workout) => {
                        const sourceColor = getSourceColor(workout.source);
                        return (
                          <div key={workout.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">💪</span>
                              <div>
                                <p className="font-medium text-gray-900">{workout.workoutType}</p>
                                <p className="text-sm text-gray-500">{formatDateItalian(workout.workoutDate)}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-gray-900">{workout.duration} min</p>
                              {workout.distance && (
                                <p className="text-sm text-gray-500">{formatDistance(workout.distance)}</p>
                              )}
                            </div>
                            <span className={`px-2 py-1 rounded text-xs ${sourceColor.bg} ${sourceColor.text}`}>
                              {getSourceLabel(workout.source)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Passi/Attività Tab */}
            {activeTab === 'passi' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-800">Attività Giornaliera</h2>
                  <button
                    onClick={() => {
                      setManualEntryType('daily');
                      setShowManualEntry(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                  >
                    + Aggiungi Manualmente
                  </button>
                </div>

                {/* Source Filter Bar */}
                {sources.length > 1 && (
                  <div className="bg-white rounded-xl shadow-sm p-4">
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Filtra per sorgente:</span>
                        <div className="flex flex-wrap gap-2">
                          {sources.map((source) => {
                            const color = getSourceColor(source);
                            const isSelected = selectedSources.length === 0 || selectedSources.includes(source);
                            return (
                              <button
                                key={source}
                                onClick={() => toggleSourceFilter(source)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                  isSelected
                                    ? `${color.bg} ${color.text} ring-2 ring-offset-1`
                                    : 'bg-gray-100 text-gray-400'
                                }`}
                              >
                                {getSourceLabel(source)}
                                {isSelected && selectedSources.length > 0 && ' ✓'}
                              </button>
                            );
                          })}
                          {selectedSources.length > 0 && (
                            <button
                              onClick={() => setSelectedSources([])}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-200 text-gray-600 hover:bg-gray-300"
                            >
                              Mostra tutti
                            </button>
                          )}
                        </div>
                      </div>
                      {hasRedundancy && (
                        <div className="flex items-center gap-2 ml-auto">
                          <span className="text-amber-600 text-sm">⚠️ Dati ridondanti</span>
                          <select
                            value={defaultSource || ''}
                            onChange={(e) => handleSetDefaultSource(e.target.value || null)}
                            className="px-2 py-1 text-xs border rounded-lg"
                          >
                            <option value="">Sorgente default: auto</option>
                            {sources.map((source) => (
                              <option key={source} value={source}>
                                Default: {getSourceLabel(source)}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {stepsChartData.length > 0 ? (
                  <>
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Passi</h3>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={stepsChartData}>
                            <defs>
                              <pattern id="stripePatternAttivita" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                                <rect width="4" height="8" fill={stripeColor1} />
                                <rect x="4" width="4" height="8" fill={stripeColor2} />
                              </pattern>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="xKey"
                              tick={{ fontSize: 12 }}
                              tickFormatter={(value) => {
                                const item = stepsChartData.find(d => d.xKey === value);
                                return item?.date || '';
                              }}
                            />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  return (
                                    <div className="bg-white p-3 shadow-lg rounded-lg border">
                                      <p className="font-medium">{data.date}</p>
                                      <p className="text-blue-600">{data.passi?.toLocaleString('it-IT')} passi</p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        Sorgente: {getSourceLabel(data.source)}
                                        {data.isRedundant && <span className="text-amber-500 ml-1">⚠️ ridondante</span>}
                                      </p>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Bar
                              dataKey="passi"
                              name="Passi"
                              fill={CHART_COLORS.steps}
                              shape={(props: unknown) => {
                                const { x, y, width, height, payload } = props as {
                                  x: number; y: number; width: number; height: number;
                                  payload: { isRedundant: boolean; source: string };
                                };
                                const fill = payload.isRedundant
                                  ? 'url(#stripePatternAttivita)'
                                  : getSourceColor(payload.source).hex || CHART_COLORS.steps;
                                return <rect x={x} y={y} width={width} height={height} fill={fill} rx={2} />;
                              }}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      {redundantMetricDates.size > 0 && (
                        <p className="text-xs text-gray-600 mt-2">
                          <span className="inline-block w-4 h-3 mr-1 align-middle rounded-sm" style={{background: `repeating-linear-gradient(45deg, ${stripeColor1}, ${stripeColor1} 2px, ${stripeColor2} 2px, ${stripeColor2} 4px)`}}></span>
                          Le barre a strisce indicano giorni con dati da più sorgenti
                        </p>
                      )}
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Calorie Attive e Minuti Attivi</h3>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={stepsChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="xKey"
                              tick={{ fontSize: 12 }}
                              tickFormatter={(value) => {
                                const item = stepsChartData.find(d => d.xKey === value);
                                return item?.date || '';
                              }}
                            />
                            <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  return (
                                    <div className="bg-white p-3 shadow-lg rounded-lg border">
                                      <p className="font-medium">{data.date}</p>
                                      <p className="text-orange-600">{data.calorie} calorie</p>
                                      <p className="text-green-600">{data.minutiAttivi} min attivi</p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        Sorgente: {getSourceLabel(data.source)}
                                        {data.isRedundant && <span className="text-amber-500 ml-1">⚠️ ridondante</span>}
                                      </p>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Legend />
                            <Line yAxisId="left" type="monotone" dataKey="calorie" stroke={CHART_COLORS.calories} name="Calorie" />
                            <Line yAxisId="right" type="monotone" dataKey="minutiAttivi" stroke={CHART_COLORS.activeMinutes} name="Min. Attivi" />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Data table */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Passi</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Calorie</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Distanza</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Min. Attivi</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sorgente</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredDailyMetrics.slice(0, 30).map((m) => {
                            const sourceColor = getSourceColor(m.source);
                            const isRedundant = redundantMetricDates.has(m.metricDate);
                            return (
                              <tr key={m.id} className={isRedundant ? 'bg-amber-50' : ''}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatDateItalian(m.metricDate)}
                                  {isRedundant && <span className="text-amber-500 ml-1">⚠️</span>}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                  {m.steps?.toLocaleString('it-IT') || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                                  {m.caloriesActive || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                                  {m.distance ? `${m.distance.toFixed(2)} km` : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                                  {m.activeMinutes || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right">
                                  <span className={`px-2 py-1 rounded text-xs ${sourceColor.bg} ${sourceColor.text}`}>
                                    {getSourceLabel(m.source)}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <p className="text-gray-500 text-lg">Nessun dato disponibile per il periodo selezionato</p>
                    <p className="text-gray-400 mt-2">Importa dati da CSV o collega un dispositivo</p>
                  </div>
                )}
              </div>
            )}

            {/* Sonno Tab */}
            {activeTab === 'sonno' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-800">Dati del Sonno</h2>
                  <button
                    onClick={() => {
                      setManualEntryType('sleep');
                      setShowManualEntry(true);
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
                  >
                    + Aggiungi Manualmente
                  </button>
                </div>

                {/* Source Filter Bar */}
                {sources.length > 1 && (
                  <div className="bg-white rounded-xl shadow-sm p-4">
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Filtra per sorgente:</span>
                        <div className="flex flex-wrap gap-2">
                          {sources.map((source) => {
                            const color = getSourceColor(source);
                            const isSelected = selectedSources.length === 0 || selectedSources.includes(source);
                            return (
                              <button
                                key={source}
                                onClick={() => toggleSourceFilter(source)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                  isSelected
                                    ? `${color.bg} ${color.text} ring-2 ring-offset-1`
                                    : 'bg-gray-100 text-gray-400'
                                }`}
                              >
                                {getSourceLabel(source)}
                                {isSelected && selectedSources.length > 0 && ' ✓'}
                              </button>
                            );
                          })}
                          {selectedSources.length > 0 && (
                            <button
                              onClick={() => setSelectedSources([])}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-200 text-gray-600 hover:bg-gray-300"
                            >
                              Mostra tutti
                            </button>
                          )}
                        </div>
                      </div>
                      {redundantSleepDates.size > 0 && (
                        <div className="flex items-center gap-2 ml-auto">
                          <span className="text-amber-600 text-sm">⚠️ Dati ridondanti</span>
                          <select
                            value={defaultSource || ''}
                            onChange={(e) => handleSetDefaultSource(e.target.value || null)}
                            className="px-2 py-1 text-xs border rounded-lg"
                          >
                            <option value="">Sorgente default: auto</option>
                            {sources.map((source) => (
                              <option key={source} value={source}>
                                Default: {getSourceLabel(source)}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {sleepChartData.length > 0 ? (
                  <>
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Fasi del Sonno (ore)</h3>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={sleepChartData} margin={{ bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="xKey"
                              interval={0}
                              tick={(props: { x: number; y: number; payload: { value: string }; index: number }) => {
                                const item = sleepChartData.find(d => d.xKey === props.payload.value);
                                const sourceColor = item ? getSourceColor(item.source).hex : '#999';
                                const prevItem = props.index > 0 ? sleepChartData[props.index - 1] : null;
                                const showDateLabel = !prevItem || prevItem.date !== item?.date;
                                return (
                                  <g transform={`translate(${props.x},${props.y})`}>
                                    <text x={0} y={0} dy={12} textAnchor="middle" fill="#666" fontSize={9}>
                                      {showDateLabel ? (item?.date || '') : ''}
                                    </text>
                                    <circle cx={0} cy={24} r={4} fill={sourceColor} />
                                  </g>
                                );
                              }}
                              height={40}
                            />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  const sourceColor = getSourceColor(data.source);
                                  return (
                                    <div className="bg-white p-3 shadow-lg rounded-lg border">
                                      <p className="font-medium">{data.date}</p>
                                      <p className="text-purple-600">Totale: {data.totale}h</p>
                                      <p className="text-indigo-600">Profondo: {data.profondo}h</p>
                                      <p className="text-indigo-400">Leggero: {data.leggero}h</p>
                                      <p className="text-purple-400">REM: {data.rem}h</p>
                                      <p className="text-xs mt-1">
                                        <span className={`inline-block px-1.5 py-0.5 rounded ${sourceColor.bg} ${sourceColor.text}`}>
                                          {getSourceLabel(data.source)}
                                        </span>
                                        {data.isRedundant && <span className="text-amber-500 ml-1">⚠️ ridondante</span>}
                                      </p>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Legend />
                            <Area type="monotone" dataKey="profondo" stackId="1" stroke={CHART_COLORS.deep} fill={CHART_COLORS.deep} name="Profondo" />
                            <Area type="monotone" dataKey="leggero" stackId="1" stroke={CHART_COLORS.light} fill={CHART_COLORS.light} name="Leggero" />
                            <Area type="monotone" dataKey="rem" stackId="1" stroke={CHART_COLORS.rem} fill={CHART_COLORS.rem} name="REM" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                      {/* Source legend */}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS.oura }}></span>
                          Oura
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS.garmin }}></span>
                          Garmin
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS.strava }}></span>
                          Strava
                        </span>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">HRV (Variabilità Frequenza Cardiaca)</h3>
                      <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={sleepChartData.filter(d => d.hrv > 0)} margin={{ bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="xKey"
                              interval={0}
                              tick={(props: { x: number; y: number; payload: { value: string }; index: number }) => {
                                const hrvData = sleepChartData.filter(d => d.hrv > 0);
                                const item = hrvData.find(d => d.xKey === props.payload.value);
                                const sourceColor = item ? getSourceColor(item.source).hex : '#999';
                                const prevItem = props.index > 0 ? hrvData[props.index - 1] : null;
                                const showDateLabel = !prevItem || prevItem.date !== item?.date;
                                return (
                                  <g transform={`translate(${props.x},${props.y})`}>
                                    <text x={0} y={0} dy={12} textAnchor="middle" fill="#666" fontSize={9}>
                                      {showDateLabel ? (item?.date || '') : ''}
                                    </text>
                                    <circle cx={0} cy={24} r={4} fill={sourceColor} />
                                  </g>
                                );
                              }}
                              height={40}
                            />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  const sourceColor = getSourceColor(data.source);
                                  return (
                                    <div className="bg-white p-3 shadow-lg rounded-lg border">
                                      <p className="font-medium">{data.date}</p>
                                      <p className="text-red-600">HRV: {data.hrv} ms</p>
                                      <p className="text-xs mt-1">
                                        <span className={`inline-block px-1.5 py-0.5 rounded ${sourceColor.bg} ${sourceColor.text}`}>
                                          {getSourceLabel(data.source)}
                                        </span>
                                        {data.isRedundant && <span className="text-amber-500 ml-1">⚠️ ridondante</span>}
                                      </p>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Line type="monotone" dataKey="hrv" stroke={CHART_COLORS.heartRate} name="HRV (ms)" dot={{ r: 4 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      {/* Source legend */}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS.oura }}></span>
                          Oura
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS.garmin }}></span>
                          Garmin
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS.strava }}></span>
                          Strava
                        </span>
                      </div>
                    </div>

                    {/* Data table */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Totale</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Profondo</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Leggero</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">REM</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Efficienza</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">HRV</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sorgente</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredSleepData.slice(0, 30).map((s) => {
                            const sourceColor = getSourceColor(s.source);
                            const isRedundant = redundantSleepDates.has(s.sleepDate);
                            return (
                              <tr key={s.id} className={isRedundant ? 'bg-amber-50' : ''}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatDateItalian(s.sleepDate)}
                                  {isRedundant && <span className="text-amber-500 ml-1">⚠️</span>}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                  {formatSleepDuration(s.totalSleepMinutes)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-indigo-600">
                                  {formatSleepDuration(s.deepSleepMinutes)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-indigo-400">
                                  {formatSleepDuration(s.lightSleepMinutes)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-purple-400">
                                  {formatSleepDuration(s.remSleepMinutes)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                                  {s.sleepEfficiency ? `${Math.round(s.sleepEfficiency)}%` : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                                  {s.hrv ? `${Math.round(s.hrv)} ms` : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right">
                                  <span className={`px-2 py-1 rounded text-xs ${sourceColor.bg} ${sourceColor.text}`}>
                                    {getSourceLabel(s.source)}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <p className="text-gray-500 text-lg">Nessun dato del sonno disponibile</p>
                    <p className="text-gray-400 mt-2">Collega un Oura Ring o importa dati</p>
                  </div>
                )}
              </div>
            )}

            {/* Frequenza Cardiaca Tab */}
            {activeTab === 'frequenzaCardiaca' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-800">Frequenza Cardiaca</h2>
                </div>

                {/* Source and Time Filter Bar */}
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <div className="flex flex-wrap items-center gap-6">
                    {/* Source Filter */}
                    {sources.length > 1 && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Sorgente:</span>
                        <div className="flex flex-wrap gap-2">
                          {sources.map((source) => {
                            const color = getSourceColor(source);
                            const isSelected = selectedSources.length === 0 || selectedSources.includes(source);
                            return (
                              <button
                                key={source}
                                onClick={() => toggleSourceFilter(source)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                  isSelected
                                    ? `${color.bg} ${color.text} ring-2 ring-offset-1`
                                    : 'bg-gray-100 text-gray-400'
                                }`}
                              >
                                {getSourceLabel(source)}
                                {isSelected && selectedSources.length > 0 && ' ✓'}
                              </button>
                            );
                          })}
                          {selectedSources.length > 0 && (
                            <button
                              onClick={() => setSelectedSources([])}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-200 text-gray-600 hover:bg-gray-300"
                            >
                              Tutti
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Time Filter */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">Orario:</span>
                      <input
                        type="time"
                        value={hrStartTime}
                        onChange={(e) => setHrStartTime(e.target.value)}
                        className="px-2 py-1 border rounded-lg text-sm w-24"
                      />
                      <span className="text-gray-400">-</span>
                      <input
                        type="time"
                        value={hrEndTime}
                        onChange={(e) => setHrEndTime(e.target.value)}
                        className="px-2 py-1 border rounded-lg text-sm w-24"
                      />
                      {(hrStartTime || hrEndTime) && (
                        <button
                          onClick={() => { setHrStartTime(''); setHrEndTime(''); }}
                          className="px-2 py-1 rounded-lg text-xs font-medium bg-gray-200 text-gray-600 hover:bg-gray-300"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {heartRateChartData.length > 0 ? (
                  <>
                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="bg-red-50 rounded-xl p-4">
                        <p className="text-sm text-gray-600">Media</p>
                        <p className="text-2xl font-bold text-red-600">{heartRateStats.avg} <span className="text-sm font-normal">bpm</span></p>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-4">
                        <p className="text-sm text-gray-600">Minimo</p>
                        <p className="text-2xl font-bold text-blue-600">{heartRateStats.min} <span className="text-sm font-normal">bpm</span></p>
                      </div>
                      <div className="bg-orange-50 rounded-xl p-4">
                        <p className="text-sm text-gray-600">Massimo</p>
                        <p className="text-2xl font-bold text-orange-600">{heartRateStats.max} <span className="text-sm font-normal">bpm</span></p>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-4">
                        <p className="text-sm text-gray-600">Rilevazioni</p>
                        <p className="text-2xl font-bold text-purple-600">{heartRateStats.count.toLocaleString()}</p>
                      </div>
                    </div>

                    {/* Heart Rate Chart */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Frequenza cardiaca</h3>
                      <div className="flex items-center gap-4 mb-4">
                        <p className="text-sm text-gray-500">
                          {isShortRange ? 'Valori puntuali' : 'Valori massimi orari'}
                        </p>
                        {/* Legend */}
                        <div className="flex gap-4 ml-auto">
                          {heartRateChartData.some(d => d.ouraHR !== null) && (
                            <div className="flex items-center gap-1.5">
                              <div className="w-4 h-0.5 bg-violet-500" />
                              <span className="text-xs text-gray-600">Oura Ring</span>
                            </div>
                          )}
                          {heartRateChartData.some(d => d.garminHR !== null) && (
                            <div className="flex items-center gap-1.5">
                              <div className="w-4 h-0.5 bg-emerald-500" />
                              <span className="text-xs text-gray-600">Garmin</span>
                            </div>
                          )}
                          {heartRateChartData.some(d => d.stravaHR !== null) && (
                            <div className="flex items-center gap-1.5">
                              <div className="w-4 h-0.5 bg-orange-500" />
                              <span className="text-xs text-gray-600">Strava</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={heartRateChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="xKey"
                              tick={{ fontSize: 11 }}
                              ticks={
                                // Get unique dates and their first occurrence index
                                heartRateChartData
                                  .filter(item => item.date) // Skip gap markers
                                  .reduce((acc, item) => {
                                    if (!acc.seenDates.has(item.date)) {
                                      acc.seenDates.add(item.date);
                                      acc.ticks.push(item.xKey);
                                    }
                                    return acc;
                                  }, { seenDates: new Set<string>(), ticks: [] as string[] })
                                  .ticks
                              }
                              tickFormatter={(value) => {
                                const item = heartRateChartData.find(d => d.xKey === value);
                                return item?.date || '';
                              }}
                            />
                            <YAxis
                              tick={{ fontSize: 12 }}
                              domain={[40, 200]}
                              ticks={[40, 80, 120, 160, 200]}
                            />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  if (!data.fullDateTime) return null; // Skip gap markers

                                  const hasOura = data.ouraHR !== null;
                                  const hasGarmin = data.garminHR !== null;
                                  const hasStrava = data.stravaHR !== null;

                                  return (
                                    <div className="bg-white p-3 shadow-lg rounded-lg border min-w-[150px]">
                                      <p className="text-sm text-gray-500 mb-2">{data.fullDateTime}</p>

                                      {hasOura && data.ouraData && (
                                        <div className="mb-2 pb-2 border-b border-gray-100 last:border-0 last:mb-0 last:pb-0">
                                          <div className="flex items-center gap-1.5 mb-1">
                                            <div className="w-2 h-2 rounded-full bg-violet-500" />
                                            <span className="text-xs font-medium text-violet-600">Oura Ring</span>
                                          </div>
                                          <p className="text-xl font-bold text-violet-600">
                                            {data.ouraHR} <span className="text-sm font-normal">bpm</span>
                                            {!isShortRange && data.ouraData.maxHeartRate && <span className="text-xs font-normal text-gray-400"> max</span>}
                                          </p>
                                          {!isShortRange && data.ouraData.maxHeartRate && (
                                            <div className="flex gap-2 text-xs text-gray-500">
                                              <span>Min: {data.ouraData.minHeartRate}</span>
                                              <span>Media: {data.ouraData.heartRate}</span>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {hasGarmin && data.garminData && (
                                        <div className="mb-2 pb-2 border-b border-gray-100 last:border-0 last:mb-0 last:pb-0">
                                          <div className="flex items-center gap-1.5 mb-1">
                                            <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                            <span className="text-xs font-medium text-emerald-600">Garmin</span>
                                          </div>
                                          <p className="text-xl font-bold text-emerald-600">
                                            {data.garminData.maxHeartRate || data.garminHR} <span className="text-sm font-normal">bpm</span>
                                            {data.garminData.maxHeartRate && <span className="text-xs font-normal text-gray-400"> max</span>}
                                          </p>
                                          {data.garminData.maxHeartRate && (
                                            <div className="flex gap-2 text-xs text-gray-500">
                                              <span>Min: {data.garminData.minHeartRate}</span>
                                              <span>Media: {data.garminData.heartRate}</span>
                                            </div>
                                          )}
                                          {data.garminData.activityType && (
                                            <p className="text-xs text-gray-400 capitalize">{data.garminData.activityType}</p>
                                          )}
                                        </div>
                                      )}

                                      {hasStrava && data.stravaData && (
                                        <div>
                                          <div className="flex items-center gap-1.5 mb-1">
                                            <div className="w-2 h-2 rounded-full bg-orange-500" />
                                            <span className="text-xs font-medium text-orange-600">Strava</span>
                                          </div>
                                          <p className="text-xl font-bold text-orange-600">
                                            {data.stravaHR} <span className="text-sm font-normal">bpm</span>
                                          </p>
                                          {data.stravaData.activityType && (
                                            <p className="text-xs text-gray-400 capitalize">{data.stravaData.activityType}</p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            {/* Oura Line */}
                            <Line
                              type="monotone"
                              dataKey="ouraHR"
                              stroke={CHART_COLORS.oura}
                              strokeWidth={2}
                              dot={false}
                              connectNulls={true}
                              activeDot={{ r: 5, fill: CHART_COLORS.oura }}
                            />
                            {/* Garmin Line */}
                            <Line
                              type="monotone"
                              dataKey="garminHR"
                              stroke={CHART_COLORS.garmin}
                              strokeWidth={2}
                              dot={false}
                              connectNulls={true}
                              activeDot={{ r: 5, fill: CHART_COLORS.garmin }}
                            />
                            {/* Strava Line */}
                            <Line
                              type="monotone"
                              dataKey="stravaHR"
                              stroke={CHART_COLORS.strava}
                              strokeWidth={2}
                              dot={false}
                              connectNulls={true}
                              activeDot={{ r: 5, fill: CHART_COLORS.strava }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Heart Rate Data Table */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-800">Dati Frequenza Cardiaca</h3>
                        <span className="text-sm text-gray-500">{filteredHeartRateData.length} rilevazioni</span>
                      </div>
                      <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data/Ora</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">HR (bpm)</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sorgente</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attività</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredHeartRateData.slice(0, 500).map((hr, index) => (
                              <tr key={hr.id || index} className="hover:bg-gray-50">
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                  {new Date(hr.timestamp).toLocaleString('it-IT', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                  })}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <span className={`text-sm font-medium ${
                                    hr.heartRate < 60 ? 'text-gray-600' :
                                    hr.heartRate < 100 ? 'text-blue-600' :
                                    hr.heartRate < 130 ? 'text-green-600' :
                                    hr.heartRate < 160 ? 'text-orange-600' :
                                    'text-red-600'
                                  }`}>
                                    {hr.heartRate}
                                  </span>
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    hr.source === 'oura' ? 'bg-violet-100 text-violet-800' :
                                    hr.source === 'strava' ? 'bg-orange-100 text-orange-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {getSourceLabel(hr.source)}
                                  </span>
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 capitalize">
                                  {hr.activityType || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {filteredHeartRateData.length > 500 && (
                          <p className="text-center text-sm text-gray-500 py-3">
                            Mostrati i primi 500 record su {filteredHeartRateData.length} totali
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Heart Rate Zones Info */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Zone di Frequenza Cardiaca</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                        <div className="p-3 rounded-lg bg-gray-100">
                          <p className="text-xs font-medium text-gray-600">Riposo</p>
                          <p className="text-sm font-bold text-gray-700">&lt; 60 bpm</p>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-100">
                          <p className="text-xs font-medium text-blue-600">Leggera</p>
                          <p className="text-sm font-bold text-blue-700">60-100 bpm</p>
                        </div>
                        <div className="p-3 rounded-lg bg-green-100">
                          <p className="text-xs font-medium text-green-600">Moderata</p>
                          <p className="text-sm font-bold text-green-700">100-130 bpm</p>
                        </div>
                        <div className="p-3 rounded-lg bg-orange-100">
                          <p className="text-xs font-medium text-orange-600">Intensa</p>
                          <p className="text-sm font-bold text-orange-700">130-160 bpm</p>
                        </div>
                        <div className="p-3 rounded-lg bg-red-100">
                          <p className="text-xs font-medium text-red-600">Massima</p>
                          <p className="text-sm font-bold text-red-700">&gt; 160 bpm</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <div className="text-6xl mb-4">❤️</div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Nessun dato sulla frequenza cardiaca</h3>
                    <p className="text-gray-500 mb-4">
                      Collega un dispositivo wearable come Oura Ring per sincronizzare i dati della frequenza cardiaca.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Allenamenti Tab */}
            {activeTab === 'allenamenti' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-800">Allenamenti</h2>
                  <button
                    onClick={() => {
                      setManualEntryType('workout');
                      setShowManualEntry(true);
                    }}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700"
                  >
                    + Aggiungi Allenamento
                  </button>
                </div>

                {/* Source Filter Bar */}
                {sources.length > 1 && (
                  <div className="bg-white rounded-xl shadow-sm p-4">
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Filtra per sorgente:</span>
                        <div className="flex flex-wrap gap-2">
                          {sources.map((source) => {
                            const color = getSourceColor(source);
                            const isSelected = selectedSources.length === 0 || selectedSources.includes(source);
                            return (
                              <button
                                key={source}
                                onClick={() => toggleSourceFilter(source)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                  isSelected
                                    ? `${color.bg} ${color.text} ring-2 ring-offset-1`
                                    : 'bg-gray-100 text-gray-400'
                                }`}
                              >
                                {getSourceLabel(source)}
                                {isSelected && selectedSources.length > 0 && ' ✓'}
                              </button>
                            );
                          })}
                          {selectedSources.length > 0 && (
                            <button
                              onClick={() => setSelectedSources([])}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-200 text-gray-600 hover:bg-gray-300"
                            >
                              Mostra tutti
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {filteredWorkouts.length > 0 ? (
                  <>
                    {/* Stats */}
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                      <div className="bg-orange-50 rounded-xl p-4">
                        <p className="text-sm text-gray-600">Totale Allenamenti</p>
                        <p className="text-2xl font-bold text-orange-600">{filteredWorkouts.length}</p>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-4">
                        <p className="text-sm text-gray-600">Tempo Totale</p>
                        <p className="text-2xl font-bold text-blue-600">{filteredWorkouts.reduce((a, w) => a + w.duration, 0)} min</p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-4">
                        <p className="text-sm text-gray-600">Distanza Totale</p>
                        <p className="text-2xl font-bold text-green-600">
                          {filteredWorkouts.reduce((a, w) => a + (w.distance || 0), 0).toFixed(1)} km
                        </p>
                      </div>
                      <div className="bg-red-50 rounded-xl p-4">
                        <p className="text-sm text-gray-600">Calorie Totali</p>
                        <p className="text-2xl font-bold text-red-600">
                          {filteredWorkouts.reduce((a, w) => a + (w.calories || 0), 0).toLocaleString('it-IT')}
                        </p>
                      </div>
                    </div>

                    {/* Pie chart */}
                    {workoutPieData.length > 0 && (
                      <div className="bg-white rounded-xl shadow-sm p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Distribuzione per Tipo</h3>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={workoutPieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                label={({ name, percent }) => `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`}
                              >
                                {workoutPieData.map((_, index) => (
                                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Workout list */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Durata</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Distanza</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Calorie</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">FC Media</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sorgente</th>
                            <th className="px-6 py-3"></th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredWorkouts.map((w) => {
                            const sourceColor = getSourceColor(w.source);
                            return (
                              <tr key={w.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatDateItalian(w.workoutDate)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {w.workoutType}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                                  {w.duration} min
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                                  {w.distance ? `${w.distance.toFixed(2)} km` : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                                  {w.calories || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                                  {w.avgHeartRate ? `${Math.round(w.avgHeartRate)} bpm` : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right">
                                  <span className={`px-2 py-1 rounded text-xs ${sourceColor.bg} ${sourceColor.text}`}>
                                    {getSourceLabel(w.source)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right">
                                  {w.source === 'manual' && (
                                    <button
                                      onClick={async () => {
                                        if (confirm('Eliminare questo allenamento?')) {
                                          await deleteWorkout(w.id);
                                          loadData();
                                        }
                                      }}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <p className="text-gray-500 text-lg">Nessun allenamento registrato</p>
                    <p className="text-gray-400 mt-2">Collega Strava o aggiungi manualmente</p>
                  </div>
                )}
              </div>
            )}

            {/* Obiettivi Tab */}
            {activeTab === 'obiettivi' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-800">I Tuoi Obiettivi</h2>
                  <button
                    onClick={() => setShowGoalModal(true)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
                  >
                    + Nuovo Obiettivo
                  </button>
                </div>

                {goals.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {goals.map((goal) => (
                      <div key={goal.id} className="bg-white rounded-xl shadow-sm p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-semibold text-gray-900">{goal.title}</h3>
                            {goal.description && (
                              <p className="text-sm text-gray-500 mt-1">{goal.description}</p>
                            )}
                          </div>
                          <button
                            onClick={async () => {
                              if (confirm('Eliminare questo obiettivo?')) {
                                await deleteGoal(goal.id);
                                loadData();
                              }
                            }}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div className="mb-2">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">{goal.currentValue} / {goal.targetValue} {goal.unit}</span>
                            <span className="font-medium text-indigo-600">{Math.round(goal.progressPercentage)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className="bg-indigo-600 h-3 rounded-full transition-all"
                              style={{ width: `${Math.min(goal.progressPercentage, 100)}%` }}
                            />
                          </div>
                        </div>
                        {goal.isAchieved && (
                          <div className="flex items-center gap-2 text-green-600 text-sm mt-2">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Obiettivo raggiunto!
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <p className="text-gray-500 text-lg">Nessun obiettivo impostato</p>
                    <p className="text-gray-400 mt-2">Crea un obiettivo per monitorare i tuoi progressi</p>
                  </div>
                )}
              </div>
            )}

            {/* Integrazioni Tab */}
            {activeTab === 'integrazioni' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-800">Dispositivi e Integrazioni</h2>
                  {(ouraStatus.connected || stravaStatus.connected) && (
                    <button
                      onClick={handleSyncAll}
                      disabled={syncing}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      {syncing ? 'Sincronizzazione...' : 'Sincronizza tutto'}
                    </button>
                  )}
                </div>

                {/* Oura Ring */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-2xl">💍</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Oura Ring</h3>
                        <p className="text-sm text-gray-500">
                          {ouraStatus.connected ? `Collegato: ${ouraStatus.email}` : 'Non collegato'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {ouraStatus.connected ? (
                        <>
                          <button
                            onClick={() => handleSync('oura')}
                            disabled={syncing}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
                          >
                            {syncing ? 'Sincronizzazione...' : 'Sincronizza'}
                          </button>
                          <button
                            onClick={() => handleSyncFullHistory('oura')}
                            disabled={syncing}
                            className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200 disabled:opacity-50"
                            title="Sincronizza tutti i dati dell'ultimo anno"
                          >
                            Storico completo
                          </button>
                          <button
                            onClick={handleOuraDisconnect}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
                          >
                            Disconnetti
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setShowOuraModal(true)}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
                        >
                          Collega
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Strava */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                        <span className="text-2xl">🚴</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Strava</h3>
                        <p className="text-sm text-gray-500">
                          {stravaStatus.connected
                            ? `Collegato: ${stravaStatus.athlete?.firstname} ${stravaStatus.athlete?.lastname}`
                            : 'Non collegato'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {stravaStatus.connected ? (
                        <>
                          <button
                            onClick={() => handleSync('strava')}
                            disabled={syncing}
                            className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
                          >
                            {syncing ? 'Sincronizzazione...' : 'Sincronizza'}
                          </button>
                          <button
                            onClick={handleStravaDisconnect}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
                          >
                            Disconnetti
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={handleStravaConnect}
                          className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700"
                        >
                          Collega con Strava
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Strava Setup Instructions */}
                  {!stravaStatus.connected && (
                    <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <h4 className="font-medium text-orange-800 mb-2 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Come collegare Strava
                      </h4>
                      <ol className="text-sm text-orange-700 space-y-2 ml-7 list-decimal">
                        <li>
                          Vai su{' '}
                          <a
                            href="https://www.strava.com/settings/api"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-orange-900 font-medium"
                          >
                            Strava API Settings
                          </a>{' '}
                          e crea una nuova applicazione
                        </li>
                        <li>
                          Inserisci come <strong>Authorization Callback Domain</strong>:{' '}
                          <code className="bg-orange-100 px-1 rounded">localhost</code> (per sviluppo) o il tuo dominio
                        </li>
                        <li>
                          Copia <strong>Client ID</strong> e <strong>Client Secret</strong> nelle{' '}
                          <a href="/settings" className="underline hover:text-orange-900 font-medium">
                            Impostazioni
                          </a>{' '}
                          dell'app (sezione Integrazioni)
                        </li>
                        <li>Clicca su "Collega con Strava" per autorizzare l'accesso</li>
                      </ol>
                    </div>
                  )}
                </div>

                {/* Garmin Connect */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-2xl">⌚</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Garmin Connect</h3>
                        <p className="text-sm text-gray-500">
                          {garminStatus.connected
                            ? `Collegato: ${garminStatus.displayName}`
                            : 'Non collegato'}
                        </p>
                        {garminStatus.lastSync && (
                          <p className="text-xs text-gray-400">
                            Ultimo sync: {formatDateItalian(garminStatus.lastSync)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {garminStatus.connected ? (
                        <>
                          <button
                            onClick={() => handleSync('garmin')}
                            disabled={syncing}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                          >
                            {syncing ? 'Sincronizzazione...' : 'Sincronizza'}
                          </button>
                          <button
                            onClick={() => handleSyncFullHistory('garmin')}
                            disabled={syncing}
                            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 disabled:opacity-50"
                            title="Sincronizza tutti i dati dell'ultimo anno"
                          >
                            Storico completo
                          </button>
                          <button
                            onClick={handleGarminDisconnect}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
                          >
                            Disconnetti
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setShowGarminModal(true)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                        >
                          Collega
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Garmin Info */}
                  {!garminStatus.connected && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Cosa puoi sincronizzare da Garmin
                      </h4>
                      <ul className="text-sm text-blue-700 space-y-1 ml-7 list-disc">
                        <li>Dati del sonno (fasi, HRV, qualità)</li>
                        <li>Passi e metriche giornaliere</li>
                        <li>Frequenza cardiaca</li>
                        <li>Attività e allenamenti</li>
                        <li>Body Battery e Stress</li>
                        <li>Respirazione e Fitness Age</li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Sync message */}
                {syncMessage && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-700">
                    {syncMessage}
                  </div>
                )}

                {/* Data Sources */}
                {sources.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Sorgenti Dati</h3>
                    <div className="space-y-3">
                      {sources.map((source) => {
                        const color = getSourceColor(source);
                        return (
                          <div key={source} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded text-sm font-medium ${color.bg} ${color.text}`}>
                                {getSourceLabel(source)}
                              </span>
                            </div>
                            <button
                              onClick={() => setShowDeleteConfirm(source)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Elimina dati
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Manual Entry Modal */}
        {showManualEntry && (
          <ManualEntryModal
            type={manualEntryType}
            onClose={() => setShowManualEntry(false)}
            onSave={async (data) => {
              if (manualEntryType === 'daily') {
                await createDailyMetric(data as Parameters<typeof createDailyMetric>[0]);
              } else if (manualEntryType === 'sleep') {
                await createSleepRecord(data as Parameters<typeof createSleepRecord>[0]);
              } else {
                await createWorkout(data as Parameters<typeof createWorkout>[0]);
              }
              setShowManualEntry(false);
              loadData();
            }}
          />
        )}

        {/* Goal Modal */}
        {showGoalModal && (
          <GoalModal
            onClose={() => setShowGoalModal(false)}
            onSave={async (data) => {
              await createGoal(data);
              setShowGoalModal(false);
              loadData();
            }}
          />
        )}

        {/* Oura Modal */}
        {showOuraModal && (
          <OuraConnectModal
            onClose={() => setShowOuraModal(false)}
            onConnect={handleOuraConnect}
          />
        )}

        {/* Garmin Modal */}
        {showGarminModal && (
          <GarminConnectModal
            onClose={() => setShowGarminModal(false)}
            onConnect={handleGarminConnect}
          />
        )}

        {/* Import Modal */}
        {showImportModal && (
          <ImportModal
            onClose={() => setShowImportModal(false)}
            onImport={handleImportCSV}
          />
        )}

        {/* Export Modal */}
        {showExportModal && (
          <ExportModal
            onClose={() => setShowExportModal(false)}
            onExport={handleExport}
            startDate={startDate}
            endDate={endDate}
          />
        )}

        {/* Delete Confirm Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Conferma Eliminazione</h3>
              <p className="text-gray-600 mb-6">
                Vuoi eliminare tutti i dati da <strong>{getSourceLabel(showDeleteConfirm)}</strong>? Questa azione non può essere annullata.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg"
                >
                  Annulla
                </button>
                <button
                  onClick={() => handleDeleteSource(showDeleteConfirm)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg"
                >
                  Elimina
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

// Manual Entry Modal Component
function ManualEntryModal({
  type,
  onClose,
  onSave,
}: {
  type: 'daily' | 'sleep' | 'workout';
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  // Daily fields
  const [steps, setSteps] = useState('');
  const [calories, setCalories] = useState('');
  const [distance, setDistance] = useState('');
  const [activeMinutes, setActiveMinutes] = useState('');

  // Sleep fields
  const [totalSleep, setTotalSleep] = useState('');
  const [deepSleep, setDeepSleep] = useState('');
  const [lightSleep, setLightSleep] = useState('');
  const [remSleep, setRemSleep] = useState('');

  // Workout fields
  const [workoutType, setWorkoutType] = useState('Corsa');
  const [duration, setDuration] = useState('');
  const [workoutDistance, setWorkoutDistance] = useState('');
  const [workoutCalories, setWorkoutCalories] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (type === 'daily') {
        await onSave({
          date,
          steps: steps ? parseInt(steps) : undefined,
          caloriesActive: calories ? parseInt(calories) : undefined,
          distance: distance ? parseFloat(distance) : undefined,
          activeMinutes: activeMinutes ? parseInt(activeMinutes) : undefined,
        });
      } else if (type === 'sleep') {
        await onSave({
          date,
          totalSleepMinutes: parseInt(totalSleep),
          deepSleepMinutes: deepSleep ? parseInt(deepSleep) : undefined,
          lightSleepMinutes: lightSleep ? parseInt(lightSleep) : undefined,
          remSleepMinutes: remSleep ? parseInt(remSleep) : undefined,
        });
      } else {
        await onSave({
          date,
          workoutType,
          duration: parseInt(duration),
          distance: workoutDistance ? parseFloat(workoutDistance) : undefined,
          calories: workoutCalories ? parseInt(workoutCalories) : undefined,
          notes: notes || undefined,
        });
      }
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Errore');
    } finally {
      setSaving(false);
    }
  };

  const titles = {
    daily: 'Aggiungi Attività Giornaliera',
    sleep: 'Aggiungi Dati Sonno',
    workout: 'Aggiungi Allenamento',
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{titles[type]}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          {type === 'daily' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Passi</label>
                <input
                  type="number"
                  value={steps}
                  onChange={(e) => setSteps(e.target.value)}
                  placeholder="es. 10000"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Calorie Attive</label>
                <input
                  type="number"
                  value={calories}
                  onChange={(e) => setCalories(e.target.value)}
                  placeholder="es. 500"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Distanza (km)</label>
                <input
                  type="number"
                  step="0.1"
                  value={distance}
                  onChange={(e) => setDistance(e.target.value)}
                  placeholder="es. 7.5"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Minuti Attivi</label>
                <input
                  type="number"
                  value={activeMinutes}
                  onChange={(e) => setActiveMinutes(e.target.value)}
                  placeholder="es. 45"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </>
          )}

          {type === 'sleep' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sonno Totale (minuti) *</label>
                <input
                  type="number"
                  value={totalSleep}
                  onChange={(e) => setTotalSleep(e.target.value)}
                  required
                  placeholder="es. 420 (7 ore)"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sonno Profondo (minuti)</label>
                <input
                  type="number"
                  value={deepSleep}
                  onChange={(e) => setDeepSleep(e.target.value)}
                  placeholder="es. 90"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sonno Leggero (minuti)</label>
                <input
                  type="number"
                  value={lightSleep}
                  onChange={(e) => setLightSleep(e.target.value)}
                  placeholder="es. 210"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">REM (minuti)</label>
                <input
                  type="number"
                  value={remSleep}
                  onChange={(e) => setRemSleep(e.target.value)}
                  placeholder="es. 90"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </>
          )}

          {type === 'workout' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Allenamento *</label>
                <select
                  value={workoutType}
                  onChange={(e) => setWorkoutType(e.target.value)}
                  required
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {workoutTypes.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Durata (minuti) *</label>
                <input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  required
                  placeholder="es. 45"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Distanza (km)</label>
                <input
                  type="number"
                  step="0.1"
                  value={workoutDistance}
                  onChange={(e) => setWorkoutDistance(e.target.value)}
                  placeholder="es. 5.0"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Calorie</label>
                <input
                  type="number"
                  value={workoutCalories}
                  onChange={(e) => setWorkoutCalories(e.target.value)}
                  placeholder="es. 350"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Note sull'allenamento..."
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
            >
              {saving ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Goal Modal Component
function GoalModal({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (data: Parameters<typeof createGoal>[0]) => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [goalType, setGoalType] = useState('steps');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetValue, setTargetValue] = useState('');
  const [targetDate, setTargetDate] = useState('');

  const selectedType = goalTypes.find((t) => t.value === goalType);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        goalType,
        title: title || selectedType?.label || goalType,
        description: description || undefined,
        targetValue: parseFloat(targetValue),
        unit: selectedType?.unit || '',
        targetDate: targetDate || undefined,
      });
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Errore');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Nuovo Obiettivo</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Obiettivo *</label>
            <select
              value={goalType}
              onChange={(e) => setGoalType(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded-lg"
            >
              {goalTypes.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titolo</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={selectedType?.label}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valore Target ({selectedType?.unit}) *
            </label>
            <input
              type="number"
              step="0.1"
              value={targetValue}
              onChange={(e) => setTargetValue(e.target.value)}
              required
              placeholder="es. 10000"
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Obiettivo (opzionale)</label>
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione (opzionale)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrizione dell'obiettivo..."
              rows={2}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg">
              Annulla
            </button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded-lg disabled:opacity-50">
              {saving ? 'Salvataggio...' : 'Crea Obiettivo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Oura Connect Modal
function OuraConnectModal({
  onClose,
  onConnect,
}: {
  onClose: () => void;
  onConnect: (token: string) => Promise<void>;
}) {
  const [token, setToken] = useState('');
  const [connecting, setConnecting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setConnecting(true);
    try {
      await onConnect(token);
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Collega Oura Ring</h3>
        <p className="text-sm text-gray-600 mb-4">
          Per collegare il tuo Oura Ring, devi creare un Personal Access Token dal tuo account Oura.
        </p>
        <ol className="text-sm text-gray-600 mb-4 list-decimal list-inside space-y-1">
          <li>Vai su <a href="https://cloud.ouraring.com/personal-access-tokens" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">cloud.ouraring.com</a></li>
          <li>Crea un nuovo Personal Access Token</li>
          <li>Copia il token e incollalo qui sotto</li>
        </ol>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Personal Access Token</label>
            <input
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
              placeholder="Incolla il token qui..."
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg">
              Annulla
            </button>
            <button type="submit" disabled={connecting || !token} className="px-4 py-2 bg-purple-600 text-white rounded-lg disabled:opacity-50">
              {connecting ? 'Connessione...' : 'Collega'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Garmin Connect Modal
function GarminConnectModal({
  onClose,
  onConnect,
}: {
  onClose: () => void;
  onConnect: (username: string, password: string) => Promise<void>;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setConnecting(true);
    setError(null);
    try {
      await onConnect(username, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore di connessione');
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Collega Garmin Connect</h3>
        <p className="text-sm text-gray-600 mb-4">
          Inserisci le credenziali del tuo account Garmin Connect per sincronizzare i dati del tuo dispositivo.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-xs text-blue-700">
            🔒 Le tue credenziali vengono criptate con AES-256 prima di essere salvate e non vengono mai condivise.
          </p>
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              placeholder="email@example.com"
              className="w-full px-3 py-2 border rounded-lg"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="w-full px-3 py-2 border rounded-lg"
              autoComplete="current-password"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg">
              Annulla
            </button>
            <button
              type="submit"
              disabled={connecting || !username || !password}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
            >
              {connecting ? 'Connessione...' : 'Collega'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Import Modal
function ImportModal({
  onClose,
  onImport,
}: {
  onClose: () => void;
  onImport: (file: File) => Promise<void>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setImporting(true);
    try {
      await onImport(file);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Importa Dati CSV</h3>
        <p className="text-sm text-gray-600 mb-4">
          Formati supportati: Apple Salute, Google Fit, Garmin, Fitbit, Samsung Health
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">File CSV</label>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg">
              Annulla
            </button>
            <button type="submit" disabled={importing || !file} className="px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50">
              {importing ? 'Importazione...' : 'Importa'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Export Modal
function ExportModal({
  onClose,
  onExport,
  startDate,
  endDate,
}: {
  onClose: () => void;
  onExport: () => Promise<void>;
  startDate: string;
  endDate: string;
}) {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      await onExport();
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Esporta Dati</h3>
        <p className="text-sm text-gray-600 mb-4">
          Esporta i dati wearable nel formato JSON per il periodo selezionato:
        </p>
        <p className="text-sm font-medium text-gray-800 mb-4">
          {formatDateItalian(startDate)} - {formatDateItalian(endDate)}
        </p>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg">
            Annulla
          </button>
          <button onClick={handleExport} disabled={exporting} className="px-4 py-2 bg-gray-600 text-white rounded-lg disabled:opacity-50">
            {exporting ? 'Esportazione...' : 'Esporta JSON'}
          </button>
        </div>
      </div>
    </div>
  );
}
